import React, {
  ReactElement,
  ReactSVGElement,
  useEffect,
  useState,
} from "react";
import clsx from "clsx";
import { IconProps, TickIcon } from "../Icon";
import Step from "./step";
interface StepsProps
  extends React.DetailedHTMLProps<
    React.HTMLAttributes<HTMLDivElement>,
    HTMLDivElement
  > {
  children: (React.ReactElement<any> | number | string)[];
  stepBoxStyle?: string;
  currentPage?: number;
  stepper?: "circular" | "linear";
  stepBoxTitle?: string;
  stepBoxDescription?: string;
  stepperWrapperStyle?: string;
}

function Steps({
  children,
  className,
  stepBoxStyle,
  currentPage = 0,
  stepper = "circular",
  stepBoxDescription,
  stepBoxTitle,
  stepperWrapperStyle
}: StepsProps) {
  const [currentPageIndex, setCurrentPageIndex] = useState(currentPage);

  useEffect(() => {
    setCurrentPageIndex(currentPage);
  }, [currentPage]);

  const childrenArray = React.Children.toArray(children) as React.ReactElement<{
    onNext: () => void;
    onPrev: () => void;
    title: string;
    description: string;
    icon?: ReactElement<IconProps>;
  }>[];

  const handleNext = () => {
    setCurrentPageIndex((prev) =>
      prev + 1 < childrenArray.length ? prev + 1 : prev
    );
  };
  const handlePrev = () => {
    setCurrentPageIndex((prev) => (prev - 1 > 0 ? prev - 1 : 0));
  };
  const stepWidthClass = (
    { 1: "w-full", 2: "w-1/2", 3: "w-1/3", 4: "w-1/4", 5: "w-1/5" } as Record<number, string>
  )[childrenArray.length] ?? "flex-1";

  return stepper === "circular" ? (
    <div className={clsx(`flex`, className)}>
      <section className="circular_steps">
        <div className={clsx("hidden md:flex flex-row", stepBoxStyle)}>
          <div className="flex flex-col justify-center items-center">
            <div
              className={clsx(
                " rounded-full w-6 h-6",
                currentPageIndex === 0
                  ? "border-2 border-primary  px-1.5 py-1.5"
                  : "bg-success"
              )}
            >
              {currentPageIndex === 0 ? (
                <div className="rounded-full w-2 h-2 bg-primary "></div>
              ) : (
                <TickIcon className="fill-current text-white" />
              )}
            </div>
            {childrenArray.map((child, index) =>
              index !== 0 ? (
                <React.Fragment key={index}>
                  <div
                    className={clsx(
                      "w-0.5 h-10",
                      index < currentPageIndex ? "bg-success" : "bg-gray-400 dark:bg-gray-600"
                    )}
                  ></div>
                  <div
                    className={clsx(
                      " rounded-full w-6 h-6",
                      index < currentPageIndex
                        ? "bg-success"
                        : index === currentPageIndex
                        ? "border-2 border-primary  px-1.5 py-1.5"
                        : "border-2 border-gray-400 dark:border-gray-500"
                    )}
                  >
                    {index < currentPageIndex ? (
                      <TickIcon className="fill-current text-white" />
                    ) : index === currentPageIndex ? (
                      <div className="rounded-full w-2 h-2 bg-primary "></div>
                    ) : (
                      <div className="rounded-full w-2 h-2"></div>
                    )}
                  </div>
                </React.Fragment>
              ) : null
            )}
          </div>
          <div
            className={clsx("hidden sm:flex flex-col justify-between  ml-4")}
          >
            {childrenArray.map((child, index) => (
              <React.Fragment key={index}>
                <div className="">
                  <div className="text-gray-700 dark:text-gray-200 font-medium text-base">
                    {child.props.title}
                  </div>
                  <div>{child.props.description}</div>
                </div>
              </React.Fragment>
            ))}
          </div>
        </div>
      </section>

      <div className="flex-1">
        <div className="flex flex-col ">
          {React.cloneElement(childrenArray[currentPageIndex], {
            onNext: handleNext,
            onPrev: handlePrev,
          })}
        </div>
      </div>
    </div>
  ) : (
    <section className={clsx("linear_steps min-w-0", className)}>
      {/* Step progress header */}
      <div className="mb-5">
        {/* Step dots row */}
        <div className="flex items-center gap-0 mb-4">
          {childrenArray.map((child, index) => (
            <React.Fragment key={index}>
              {/* Step circle */}
              <div className="flex flex-col items-center">
                <div
                  className={clsx(
                    "h-8 w-8 rounded-full flex items-center justify-center text-xs font-bold shrink-0 transition-colors",
                    index < currentPageIndex
                      ? "bg-green-500 text-white"
                      : index === currentPageIndex
                      ? "bg-primary text-white ring-4 ring-primary/20"
                      : "bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500"
                  )}
                >
                  {index < currentPageIndex ? (
                    <TickIcon className="fill-current text-white" size={4} />
                  ) : (
                    index + 1
                  )}
                </div>
                <span
                  className={clsx(
                    "text-[10px] font-semibold mt-1 text-center leading-tight max-w-[56px] hidden sm:block",
                    index === currentPageIndex
                      ? "text-primary dark:text-primary-300"
                      : index < currentPageIndex
                      ? "text-green-600 dark:text-green-400"
                      : "text-gray-400 dark:text-gray-500"
                  )}
                >
                  {child.props.title}
                </span>
              </div>
              {/* Connector line */}
              {index < childrenArray.length - 1 && (
                <div
                  className={clsx(
                    "flex-1 h-0.5 mx-1",
                    index < currentPageIndex
                      ? "bg-green-500"
                      : "bg-gray-200 dark:bg-gray-700"
                  )}
                />
              )}
            </React.Fragment>
          ))}
        </div>
        {/* Current step label on mobile */}
        <p className="text-xs font-semibold text-primary dark:text-primary-300 sm:hidden">
          Step {currentPageIndex + 1} of {childrenArray.length}:{" "}
          {childrenArray[currentPageIndex]?.props.title}
        </p>
      </div>

      {React.cloneElement(childrenArray[currentPageIndex], {
        onNext: handleNext,
        onPrev: handlePrev,
      })}
    </section>
  );
}

export default Steps;
